/**
 * Audio Content Slide
 * Displays audio player with optional transcript.
 * Includes error handling for CORS-blocked audio sources.
 */

import { useState } from 'react';
import type { ContentSlideProps } from '../types';
import { stripHtml } from '../../../../utils/slides/stripHtml';

export default function AudioSlide({ slide, className = '' }: ContentSlideProps) {
  const { content } = slide;
  const [error, setError] = useState(false);

  return (
    <div className={`mb-8 max-w-2xl mx-auto ${className}`}>
      {content?.showTitle !== false && content?.title && (
        <h2 className="text-3xl font-bold mb-4 text-center">
          {stripHtml(content.title)}
        </h2>
      )}
      {content?.audioUrl && !error && (
        <>
          {content.audioTitle && content.audioTitle !== content.title && (
            <p className="text-xl font-semibold mb-4">🎵 {content.audioTitle}</p>
          )}
          <audio
            src={content.audioUrl}
            controls={content.showControls !== false}
            autoPlay={!content.showControls}
            preload="metadata"
            className="w-full mb-4 rounded-xl"
            onError={() => setError(true)}
          />
        </>
      )}

      {/* Fallback when audio fails to load */}
      {content?.audioUrl && error && (
        <div className="w-full rounded-2xl border-2 border-border shadow-lg bg-muted flex flex-col items-center justify-center gap-4 px-6 py-8 text-center mb-4">
          <div className="text-4xl">🔇</div>
          <p className="text-lg font-semibold text-foreground">Audio could not be loaded</p>
          <p className="text-sm text-muted-foreground">
            The audio source may have blocked playback from this site. You can listen to it directly instead.
          </p>
          <a
            href={content.audioUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
          >
            Open audio in new tab ↗
          </a>
        </div>
      )}

      {content?.audioUrl && (
        <div className="text-right">
          <a
            href={content.audioUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
          >
            Open in new tab ↗
          </a>
        </div>
      )}

      {content?.transcript && (
        <div className="mt-6 p-4 bg-muted rounded-xl text-left">
          <p className="text-sm font-semibold mb-2">Transcript / Notes:</p>
          <p className="text-sm whitespace-pre-wrap">{content.transcript}</p>
        </div>
      )}
    </div>
  );
}
